export interface ChatbotContext {
  page?: string;
  shipmentNumber?: string;
  accountId?: string;
  warehouseId?: string;
  systemId?: string;
  carrierId?: string;
  serviceId?: string;
  adminId?: string;
  userId?: string;
  chargeId?: string;
  squadId?: string;
  routeId?: string;
  tripId?: string;
  invoiceId?: string;
  [key: string]: unknown;
}

export function getChatbotContext(location: { pathname: string; search: string }): ChatbotContext {
  const { pathname, search } = location;
  const searchParams = new URLSearchParams(search);
  const context: ChatbotContext = {};

  // Helper query parameter extractors
  const ref = searchParams.get('ref');
  const accountId = searchParams.get('accountId');
  const idQuery = searchParams.get('id');

  // 1. Shipment Details
  const historyDetailMatch = pathname.match(/^\/shipment\/history\/([^/]+)$/);
  if (historyDetailMatch) {
    context.page = 'shipment details page';
    context.shipmentNumber = historyDetailMatch[1];
    return context;
  }

  const adminEditMatch = pathname.match(/^\/shipment\/admin\/edit\/([^/]+)$/);
  if (adminEditMatch) {
    context.page = 'shipment details page';
    context.shipmentNumber = adminEditMatch[1];
    return context;
  }

  const unprocessedMatch = pathname.match(/^\/shipment\/admin\/unprocessed\/edit\/([^/]+)$/);
  if (unprocessedMatch) {
    context.page = 'shipment details page';
    context.shipmentNumber = unprocessedMatch[1];
    return context;
  }

  // 2. Create Shipment
  if (pathname === '/shipment' || pathname === '/shipment/create-single') {
    context.page = 'create shipment page';
    if (ref) context.shipmentNumber = ref;
    if (accountId) context.userId = accountId;
    return context;
  }

  // 3. Shipment list pages
  if (pathname === '/shipment/history' || pathname === '/shipment/admin/history') {
    context.page = 'shipment history page';
    return context;
  }

  if (pathname === '/shipment/draft') {
    context.page = 'shipment draft page';
    return context;
  }

  // 4. Invoices
  if (pathname === '/invoice' || pathname === '/invoice/list') {
    context.page = 'invoice history page';
    return context;
  }

  if (pathname === '/invoice/view') {
    context.page = 'invoice view page';
    if (idQuery) context.invoiceId = idQuery;
    return context;
  }

  if (pathname === '/purchase-invoices' || pathname === '/purchase-invoices/list') {
    context.page = 'purchase invoice history page';
    return context;
  }

  // 5. Warehouse edits
  const warehouseEditMatch = pathname.match(/^\/warehouse\/edit\/([^/]+)$/);
  if (warehouseEditMatch) {
    context.page = 'edit warehouse page';
    context.warehouseId = warehouseEditMatch[1];
    return context;
  }

  // 6. System edits
  const systemEditMatch = pathname.match(/^\/system\/edit\/([^/]+)$/);
  if (systemEditMatch) {
    context.page = 'edit system page';
    context.systemId = systemEditMatch[1];
    return context;
  }

  // 7. Carrier and Service edits
  const carrierEditMatch = pathname.match(/^\/carrier-and-services\/edit\/carrier\/([^/]+)$/);
  if (carrierEditMatch) {
    context.page = 'edit carrier page';
    context.carrierId = carrierEditMatch[1];
    return context;
  }

  const serviceEditMatch = pathname.match(/^\/carrier-and-services\/edit\/service\/([^/]+)$/);
  if (serviceEditMatch) {
    context.page = 'edit service page';
    context.serviceId = serviceEditMatch[1];
    return context;
  }

  // 8. Admin edits
  const adminEditRouteMatch = pathname.match(/^\/admin\/edit\/([^/]+)$/);
  if (adminEditRouteMatch) {
    context.page = 'edit admin page';
    context.adminId = adminEditRouteMatch[1];
    return context;
  }

  // 9. User edits
  const userEditMatch = pathname.match(/^\/user\/edit\/([^/]+)$/);
  if (userEditMatch) {
    context.page = 'edit user page';
    context.userId = userEditMatch[1];
    return context;
  }

  // 10. Global charges edits
  const chargeEditMatch = pathname.match(/^\/global-charges\/edit\/([^/]+)$/);
  if (chargeEditMatch) {
    context.page = 'edit global charge page';
    context.chargeId = chargeEditMatch[1];
    return context;
  }

  // 11. Squad edits
  const squadEditMatch = pathname.match(/^\/squad\/edit\/([^/]+)$/);
  if (squadEditMatch) {
    context.page = 'edit squad page';
    context.squadId = squadEditMatch[1];
    return context;
  }

  // 12. Route Optimizer
  const routeOptMatch = pathname.match(/^\/route-optimizer\/details\/([^/]+)\/([^/]+)$/);
  if (routeOptMatch) {
    context.page = 'route optimizer details page';
    context.routeId = routeOptMatch[1];
    context.tripId = routeOptMatch[2];
    return context;
  }

  // 13. Squad reports details
  const squadReportMatch = pathname.match(/^\/squad-reports\/details\/([^/]+)$/);
  if (squadReportMatch) {
    context.page = 'squad report details page';
    context.squadId = squadReportMatch[1];
    return context;
  }

  // Static General Routes mapping
  const routeMap: Record<string, string> = {
    '/': 'home page',
    '/profile': 'profile page',
    '/get-rates': 'get rates page',
    '/my-rates': 'my rates page',
    '/track-shipment': 'track shipment page',
    '/manifest': 'manifest page',
    '/warehouse': 'warehouse list page',
    '/warehouse/create': 'create warehouse page',
    '/system': 'system list page',
    '/system/create': 'create system page',
    '/carrier-and-services': 'carrier and services list page',
    '/carrier-and-services/create/carrier': 'create carrier page',
    '/carrier-and-services/create/service': 'create service page',
    '/admin': 'admin list page',
    '/admin/create': 'create admin page',
    '/user': 'user list page',
    '/user/create-account': 'create user page',
    '/pricing': 'pricing contract page',
    '/pricing/contract': 'pricing contract page',
    '/pricing/tariff': 'pricing tariff page',
    '/pricing/base': 'pricing base page',
    '/pricing/tariff/create': 'create tariff pricing page',
    '/pricing/contract/create': 'create contract pricing page',
    '/pricing/base/create': 'create base pricing page',
    '/pricing/tariff/edit': 'edit tariff pricing page',
    '/pricing/contract/edit': 'edit contract pricing page',
    '/pricing/base/edit': 'edit base pricing page',
    '/global-charges': 'global charges list page',
    '/global-charges/create': 'create global charge page',
    '/transactions': 'transactions list page',
    '/transactions/list': 'transactions list page',
    '/transactions/wallet': 'wallet transactions page',
    '/transactions/credit': 'credit transactions page',
    '/transactions/credits-and-wallets': 'credits and wallets page',
    '/squad': 'squad list page',
    '/squad/create': 'create squad page',
    '/route-optimizer': 'route optimizer list page',
    '/tasks': 'tasks list page',
    '/squad-reports': 'squad reports list page',
    '/access-group': 'access groups list page',
    '/access-group/create': 'create access group page',
    '/access-group/edit': 'edit access group page',
    '/notifications-settings': 'notification settings page',
    '/organization-setup': 'organization setup page',
    '/organization-setup/system': 'organization setup system page',
    '/organization-setup/warehouse': 'organization setup warehouse page',
    '/organization-setup/org-settings': 'organization setup settings page',
    '/organization-setup/kyc-settings': 'organization setup kyc page',
    '/organization-setup/invoice-settings': 'organization setup invoice page',
    '/organization-setup/shipment-settings': 'organization setup shipment page',
    '/organization-setup/complete': 'organization setup complete page',
  };

  if (routeMap[pathname]) {
    context.page = routeMap[pathname];
    return context;
  }

  // Return empty context if pathname is not explicitly recognized
  return context;
}
